import * as React from 'react';
import {Column, EditableValue} from "../components/BasicDataGrid";
import SelectFieldViewOnlyColumn, {SelectFieldViewOnlyColumnPropsDefinition} from "../components/SelectFieldViewOnlyColumn";
import SelectFieldColumn, {SelectFieldColumnPropsDefinition} from "../components/SelectFieldColumn";
import DerivateOptionColumn, {DerivateOptionColumnDefinition, DerivateOptionColumnHeaderRenderer} from "../components/DerivateOptionColumn";
import {ModelyearDto, ModeltypeBasicDto, OptionDto, OptionBasicDto} from "./Client";
import TextFieldRegExColumn, {TextFieldRegExColumnPropsDefinition} from "../components/TextFieldRegExColumn";
import {ITranslatedComponent} from "../redux/reducers/generalReducer";
import {translate} from "react-i18next";
import TextFieldColumn, {TextFieldColumnPropsDefinition} from '../components/TextFieldColumn';
import MultiLineColumn, {MultiLineColumnPropsDefinition} from '../components/MultiLineColumn';
import {DerivateUtils} from "../components/Table/components/derivate/DerivateUtils";

/** this error is thrown, if some mandatory properties are not defined */
class ColumnError extends Error {
}

interface INameHeaderProps extends ITranslatedComponent {
    value: string;
}
class NameHeader extends React.Component<INameHeaderProps> {
    render() {
        return <div>{this.props.t(this.props.value.toUpperCase())}</div>;
    }
}
const NameHeaderTranslated = translate("COLUMN_FACTORY")(NameHeader);

export class RegexFactory {
    static readonly regex = {
        btyp: '^$|^[A-Z0-9]{6}$',
        name: '.{1,64}',
        optionCode: '^[a-zA-Z0-9#]{0,5}$',
        updateOptionCode: '^[a-zA-Z0-9]{1,5}$',
        partNumber: '^[a-zA-Z0-9]{1,15}$'
    };
}

export class ColorFactory {
    /** 16 color every browser supports for max 16 workflow states. */
    private static readonly _colors = [
        "aqua", "lime", "blue", "green", "silver", "red", "maroon", "teal", "blue",
        "white", "fuchsia", "olive", "yellow", "gray", "navy", "purple", "black"
    ];
    public static color(sequenceId: number) {
        return this._colors[sequenceId % this._colors.length];
    }
}
// TODO XSVX001-200 This component needs to be refactored
// as some of the logic is not used anymore
export default class ColumnFactory {

    /** define here properties, which are the same additional properties, but can be overwritten */
    private static getStaticProperties(key: string): object {
        let staticProps: any = {};
        switch (key) {
            case "_approval":
                staticProps.minRowHeight = 100;
                break;
            case "btyp":
                staticProps.regex = RegexFactory.regex.btyp;
                break;
            case "name":
                staticProps.width = 300;
                staticProps.regex = RegexFactory.regex.name;
                break;
            case "typefamily":
                staticProps.width = 250;
                break;
            case "specification":
                staticProps.dataDelimitter = ";";
                staticProps.multi = true;
                staticProps.width = 250;
                break;
            case "value":
                staticProps.width = 350;
                break;
            case "longDescription":
                staticProps.width = 400;
                break;
            case "ser":
                staticProps.clearable = false;
                staticProps.dataSource = [false, true];
                break;
            case "optionType":
                staticProps.clearable = false;
                staticProps.sortable = true;
                staticProps.groupable = true;
                staticProps.keyEditable = "statusValueStateEditable";
                break;
            case "optionTypeView":
                staticProps.width = 80;
                staticProps.sortable = true;
                staticProps.groupable = true;
                staticProps.keyEditable = EditableValue.Readonly;
                break;
            case "optionCode":
                staticProps.width = 90;
                staticProps.regex = RegexFactory.regex.optionCode;
                staticProps.keyEditable = "statusValueStateEditable";
                break;
            case "optionBasicView":
                staticProps.width = 80;
                staticProps.sortable = true;
                staticProps.keyEditable = EditableValue.Readonly;
                break;
            case "references":
                staticProps.width = 80;
                staticProps.keyEditable = EditableValue.Readonly;
                break;
            case "optionIndicators":
                staticProps.width = 80;
                staticProps.keyEditable = EditableValue.Readonly;
                break;
            case "objectId":
                staticProps.keyEditable = EditableValue.Readonly;
                break;
            case "price":
                staticProps.width = 100;
                staticProps.keyEditable = EditableValue.Editable;
                break;
            case "gear":
                staticProps.width = 55;
                break;
            case "techValue":
                staticProps.width = 370;
                break;
            case "metadata":
                staticProps.width = 260;
                break;
            case "category":
                staticProps.groupable = true;
                staticProps.exactFiltering = true;
                staticProps.keyEditable = EditableValue.Readonly;
                break;
            case "categoryText":
                staticProps.groupable = true;
                staticProps.width = 110;
                staticProps.keyEditable = EditableValue.Readonly;
                break;
            case "shortTexts":
            case "longTexts":
                staticProps.minRowHeight = 140;
                staticProps.width = 200;
                staticProps.editable = EditableValue.Disabled;
                break;
            case "shorttext":
                staticProps.width = 300;
                staticProps.minRowHeight = 180;
                staticProps.keyEditable = "statusValueStateEditable";
                break;
            case "longtext":
                staticProps.width = 500;
                staticProps.minRowHeight = 180;
                staticProps.keyEditable = "statusValueStateEditable";
                break;
            case "footer":
            case "usp":
                staticProps.minRowHeight = 180;
                staticProps.width = 350;
                staticProps.keyEditable = "statusValueStateEditable";
                break;
            case "constraint":
            case "exclusion":
            case "comment":
            case "pdiptext":
                staticProps.width = 400;
                staticProps.multiLine = true;
                staticProps.keyEditable = EditableValue.Readonly;
                break;
            case "statusView":
                staticProps.groupable = true;
                staticProps.width = 150;
                staticProps.keyEditable = EditableValue.Readonly;
                staticProps.valueState = {
                    key: "statusValueState",
                    keyEditable: "statusValueStateEditable",
                    states: "statusValueStates",
                };
                break;
            case "derivateStates":
                staticProps.resizable = false;
                staticProps.keyEditable = "statusValueStateEditable";
                break;
            case "partNumber":
                staticProps.regex = RegexFactory.regex.partNumber;
                staticProps.keyEditable = "statusValueStateEditable";
                break;
            default:
                // do nothing
                break;
        }
        return staticProps;
    }

    public static getColumnWithParameters<T extends object>(key: string, moreValues: T, noException: boolean = false): Column {
        let retVal: Column;

        let colCommon: any = {
            key: key,
            name: 'Name_' + key,
            headerRenderer: <NameHeaderTranslated value={key} />,
            sortable: false,
            filterable: true,
            exactFiltering: false,
            resizable: true,
            width: 100,
            minRowHeight: 40,
            keyEditable: EditableValue.Editable,
            events: {
                // the columns should focus their content at a single click
                onClick: (ev: React.SyntheticEvent<any>, args: {rowIdx: number, idx: number, rowId: number, column: Column}): void => {
                    const grid = (args && args.column && args.column.getGrid) ? args.column.getGrid() : undefined;
                    if (grid) {
                        grid.openCellEditor(args.rowIdx, args.idx);
                    }
                }
            },
            ...ColumnFactory.getStaticProperties(key)
        };

        let requiredProps: string[] = [];
        // IMPLEMENTATION: add this to col:
        // ...colCommon,
        // put here properties to be overwritten by moreValues
        // ...moreValues as object,
        // put here properties to be not overwritten
        switch (key) {
            case "specification":
                {
                    const col: SelectFieldViewOnlyColumnPropsDefinition =
                        {
                            ...colCommon,
                            ...moreValues as object,
                            cellRenderer: SelectFieldViewOnlyColumn,
                        };
                    retVal = col;
                }
                break;
            case "ser":
            case "optionType":
            case "typefamily":
                requiredProps.push('dataSource');
                if (noException) {
                    Object.assign(colCommon, {dataSource: []});
                }
                {
                    const col: SelectFieldColumnPropsDefinition =
                        {
                            ...colCommon,
                            clearable: false,
                            valueComparator: ColumnFactory.valueComparator,
                            ...moreValues as object,
                            cellRenderer: SelectFieldColumn,
                        };
                    retVal = col;
                }
                break;
            case "derivateStates":
                requiredProps.push('derivates');
                if (noException) {
                    Object.assign(colCommon, {derivates: []});
                }
                {
                    const {derivates = []} = moreValues as {derivates: string[]};
                    const col: DerivateOptionColumnDefinition =
                        {
                            ...colCommon,
                            width: derivates.length * 25, // set width, but let it be overwritten
                            ...moreValues as object,
                            cellRenderer: DerivateOptionColumn,
                            headerRenderer: DerivateOptionColumnHeaderRenderer,
                        };
                    retVal = col;
                }
                break;
            case "btyp":
            case "name":
            case "optionCode":
            case "partNumber":
                requiredProps.push('regex');
                colCommon.sortable = true;
                if (noException) {
                    Object.assign(colCommon, {regex: ".*"});
                }
                {
                    const col: TextFieldRegExColumnPropsDefinition =
                        {
                            ...colCommon,
                            ...moreValues as object,
                            cellRenderer: TextFieldRegExColumn,
                        };
                    retVal = col;
                }
                break;
            case "footer":
            case "usp":
            case "shorttext":
            case "shortTexts":
            case "longtext":
            case "longTexts":
                {
                    const col: MultiLineColumnPropsDefinition =
                        {
                            ...colCommon,
                            ...moreValues as object,
                            cellRenderer: MultiLineColumn,
                        };
                    retVal = col;
                }
                break;
            case "constraint":
            case "exclusion":
            case "comment":
            case "price":
                colCommon.sortable = true;
                {
                    const col: TextFieldColumnPropsDefinition =
                    {
                        ...colCommon,
                        ...moreValues as object,
                        cellRenderer: TextFieldColumn,
                    };
                    retVal = col;
                }
                break;
            case "pdiptext":
                {
                    const col: TextFieldColumnPropsDefinition =
                    {
                        ...colCommon,
                        ...moreValues as object,
                        cellRenderer: TextFieldColumn,
                    };
                    retVal = col;
                }
                break;
            default:
                colCommon.sortable = true;
                {
                    const col: Column =
                        {
                            ...colCommon,
                            ...moreValues as object,
                        };
                    retVal = col;
                }
                break;
        }

        // check for required properties are all defined
        const notFoundProps = requiredProps.filter(rq => !(rq in retVal));
        switch (notFoundProps.length) {
            case 0:
                // do nothing
                break;
            case 1:
                throw new ColumnError("Property '" + notFoundProps[0] + "' is missing for column '" + key + "'!");
            default:
                throw new ColumnError("Properties " + JSON.stringify(notFoundProps) + " are missing for column '" + key + "'!");
        }
        return retVal;
    }

    public static valueComparator(actual: any, expected: any): boolean {
        return typeof actual === 'object' && ('objectId' in actual || 'objectId' in expected) ? actual.objectId === expected.objectId : actual === expected;
    }

    public static getColumn(key: string, noException: boolean = false): Column {
        return this.getColumnWithParameters<{}>(key, {}, noException);
    }

    public static isDerivateColumn(column: Column): column is DerivateOptionColumnDefinition {
        return column.derivates !== undefined;
    }

    public static isSelectFieldColumn(column: Column): column is SelectFieldColumnPropsDefinition {
        return column.dataSource !== undefined;
    }

    public static mj2str(modelYear: ModelyearDto): string {
        return modelYear.mj1 + ' (' + modelYear.mj4 + ')';
    }

    public static mt2str(val: ModeltypeBasicDto, userLanguage: string): string {
        const name = DerivateUtils.renderDerivateName(val, userLanguage);
        return val.btyp ? val.btyp : name ? name : "<modeltype> " + val.objectId;
    }

    public static optionBasic2str(val: OptionBasicDto): string {
        return val.optionCode ? val.optionCode : "<optionBasic> " + val.objectId;
    }

    public static option2str(val: OptionDto): string {
        return val.optionBasic && val.optionBasic.optionCode ? val.optionBasic.optionCode : "<option> " + val.objectId;
    }

    public static deepCopy<T>(val: T): T {
        return JSON.parse(JSON.stringify(val));
    }
}



// WEBPACK FOOTER //
// ./src/services/ColumnFactory.tsx